<template>
  <div class="mars-scheduler-filter">
    <div
      class="d-flex flex-row justify-content-between align-items-center bg-light-gray min-width-fit-content"
    >
      <div class="d-flex flex-wrap align-items-center">
        <span class="ml-4 mr-3" style="font-size: 14px;">Active filters:</span>
        <div
          v-if="date"
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Date:
              <span class="color-filter">{{ formatDateOnly(date) }}</span></span
            >
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(m, mIndex) in marsStatus"
          :key="m.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3">
              Status:
              <span class="color-filter">{{ m.label }}</span>
            </span>
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('marsStatus', mIndex)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(c, cIndex) in eoc"
          :key="c.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Patient: <span class="color-filter">{{ c.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('eoc', cIndex)"
            ></i>
          </div>
        </div>
        <div
          class="filter-value bg-white border pr-2 pt-1 pb-1 m-1"
          v-for="(m, mIndex) in medication"
          :key="m.value"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span class="mr-3"
              >Medication: <span class="color-filter">{{ m.label }}</span></span
            >
            <i
              class="fa fa-times text-danger hover-pointer hover-text-almost-black"
              @click="removeIndexFromFilter('medication', mIndex)"
            ></i>
          </div>
        </div>
        <span
          v-if="hasFilters"
          class="clear-all-filters text-danger ml-2"
          style="font-size: 14px;"
          @click="clearAllFilters"
          >Clear all filters</span
        >
      </div>
      <div class="d-flex align-self-stretch align-items-center">
        <div class="align-self-stretch" style="padding: 9px 0px;">
          <div class="vertical-separator border-med-light-gray"></div>
        </div>
        <b-dropdown
          id="dropdown-form"
          text="Filters"
          ref="filterDropdown"
          class="container-main bg-light-gray"
          toggle-class="d-flex flex-row justify-content-between align-items-lg-center bg-transparent border-0 color-custom-dark-grey dropdown-menu-label"
          menu-class="container-filter-main-menu"
          toggle-tag="div"
          boundary="window"
          right
          @hide="onFilterHide"
        >
          <template #button-content>
            <div class="d-flex align-items-center mr-2">
              <i
                class="fas fa-filter text-primary mr-2"
                style="font-size: 15px;"
                aria-hidden="true"
              ></i>
              <span style="font-size: 14px;">Filters</span>
            </div>
          </template>
          <b-dropdown-form class="px-2">
            <b-form-group label="Filter by:">
              <b-form-select
                v-model="selectedFilter"
                :options="filterOptions"
              />
            </b-form-group>
            <div
              v-if="selectedFilter == 'date'"
              class="d-flex flex-row justify-content-between"
            >
              <div>
                <b-form-group label="Date" class="mr-4 ml-4">
                  <b-calendar
                    v-model="date"
                    :date-format-options="{
                      year: 'numeric',
                      day: '2-digit',
                      month: '2-digit'
                    }"
                    width="200px"
                  >
                  </b-calendar>
                </b-form-group>
              </div>
            </div>

            <multiple-select-filter
              v-if="selectedFilter == 'status'"
              v-model="marsStatus"
              :options="marsStatuses"
              :label="'Status'"
              :width="'250px'"
              :showSearch="false"
              :key="'status'"
            />

            <multiple-select-filter
              v-if="selectedFilter == 'patient'"
              class="eoc-filter"
              v-model="eoc"
              :label="'Patients'"
              :options="clientOptions"
              :showSearch="true"
              :async="true"
              :searching="searchingClients"
              @on-search="fetchClientOptions"
              @on-text-selection="updateTextSelection"
              :width="'var(--filter-eoc-width)'"
              :key="'patient'"
              ref="patientFilter"
            >
              <template v-slot:item-option="{ itemOption, selected }">
                <eoc-filter-option-template
                  :eoc="itemOption.data"
                  :selected="selected"
                />
              </template>
            </multiple-select-filter>
            <multiple-select-filter
              v-if="selectedFilter == 'medication'"
              class="medication-filter"
              v-model="medication"
              :label="'Medications'"
              :options="medications"
              :showSearch="true"
              :searching="searchingMedications"
              @on-search="searchMedications"
              @on-text-selection="updateTextSelection"
              :width="'var(--filter-medication-width)'"
              :key="'medication'"
              ref="medicationFilter"
            >
              <template v-slot:item-option="{ itemOption, selected }">
                <div
                  class="d-flex flex-row justify-content-between align-items-center mb-1 pl-3 pr-3 pt-1 pb-1"
                >
                  <span>{{ itemOption.label }}</span>
                  <img
                    v-show="selected"
                    :src="require('../../assets/icons/check.png')"
                  />
                </div>
              </template>
            </multiple-select-filter>
          </b-dropdown-form>
        </b-dropdown>
      </div>
    </div>
  </div>
</template>

<script>
import eocServer from "../../server/eoc";
import medicationServer from "../../server/medication";
import EocFilterOptionTemplate from "../../views/EocFilterOptionTemplate";

export default {
  name: "MarsSchedulerFilter",
  components: { EocFilterOptionTemplate },
  props: ["value", "showing"],
  data() {
    return {
      selectedFilter: "date",
      filterOptions: [
        {
          value: "date",
          text: "Date"
        },
        {
          value: "status",
          text: "Status"
        },
        {
          value: "patient",
          text: "Patient"
        },
        {
          value: "medication",
          text: "Medication"
        }
      ],
      marsStatuses: [
        {
          value: "Pending",
          label: "Pending"
        },
        {
          value: "Given",
          label: "Given"
        },
        {
          value: "NotGiven",
          label: "Not Given"
        }
      ],
      clientOptions: [],
      medications: [],
      searchingClients: false,
      searchingMedications: false,
      filterTextSelection: null
    };
  },
  methods: {
    async fetchClientOptions(search) {
      if (this.lastClientTimeout) {
        clearTimeout(this.lastClientTimeout);
        this.lastClientTimeout = null;
      }
      this.lastClientTimeout = setTimeout(async () => {
        if (!search || search.length == 0 || search.length >= 3) {
          this.searchingClients = true;
          const optionsResult = await eocServer.queryList(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],
              page: 1,
              pageSize: 50
            },
            { "Hide-Loading": true }
          );

          this.clientOptions = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: this.formatEocObj(eoc),
            data: eoc
          }));

          this.searchingClients = false;
        }
      }, 1000);
    },
    async searchMedications(search) {
      if (this.lastMedicationTimeout) {
        clearTimeout(this.lastMedicationTimeout);
        this.lastMedicationTimeout = null;
      }
      this.lastMedicationTimeout = setTimeout(async () => {
        if (search && search.length >= 3) {
          this.searchingMedications = true;
          const medications = await medicationServer.query(
            { searchPhrase: search, page: 0, pageSize: 50, orderBy: "name" },
            { "Hide-Loading": true }
          );
          this.medications = medications.map(m => ({
            value: m.id,
            label: m.name
          }));
          this.searchingMedications = false;
        } else {
          this.medications = [];
        }
      }, 1000);
    },
    removeIndexFromFilter(filterName, index) {
      const filter = [...this.value[filterName]];
      filter.splice(index, 1);
      this.$emit("input", { ...this.value, [filterName]: filter });
    },
    clearAllFilters() {
      this.$emit("input", {
        ...this.value,
        eoc: [],
        medication: [],
        marsStatus: []
      });
    },
    onFilterHide(event) {
      let ref = null;
      if (this.selectedFilter == "medication") {
        ref = this.$refs.medicationFilter;
      }
      if (this.selectedFilter == "patient") {
        ref = this.$refs.patientFilter;
      }
      if (this.filterTextSelection) {
        if (this.filterTextSelection) event.preventDefault();
        ref.highlightText();
        this.filterTextSelection = null;
      }
    },
    updateTextSelection(textSelection) {
      this.filterTextSelection = textSelection;
    }
  },
  computed: {
    date: {
      get() {
        return this.value.date;
      },
      set(value) {
        this.$emit("input", { ...this.value, date: value });
      }
    },
    marsStatus: {
      get() {
        return this.value.marsStatus;
      },
      set(value) {
        this.$emit("input", { ...this.value, marsStatus: value });
      }
    },
    eoc: {
      get() {
        return this.value.eoc;
      },
      set(value) {
        this.$emit("input", { ...this.value, eoc: value });
      }
    },
    medication: {
      get() {
        return this.value.medication;
      },
      set(value) {
        this.$emit("input", { ...this.value, medication: value });
      }
    },
    hasFilters() {
      return (
        this.value.eoc.length > 0 ||
        this.value.medication.length > 0 ||
        this.value.marsStatus.length > 0
      );
    }
  },
  watch: {
    selectedFilter: function() {
      this.$refs.filterDropdown.updatePopper();
    },
    showing: function(newValue) {
      if (newValue) {
        this.fetchClientOptions("");
      }
    }
  }
};
</script>

<style scoped lang="scss">
.mars-scheduler-filter {
  --filter-date-width: 250px;
  --filter-eoc-width: 430px;
  --filter-medication-width: 250px;
  overflow-x: scroll;
  flex-shrink: 0;

  .container-main {
    height: 45px;

    &:hover {
      cursor: pointer;
    }
  }

  .date-filter {
    width: var(--filter-date-width);
  }

  .eoc-filter {
    width: var(--filter-eoc-width);
  }

  .medication-filter {
    width: var(--filter-medication-width);
  }

  .filter-value-container {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .filter-value {
    min-width: 100px;
    height: 29px;
    color: #a6a6a6;
    font-size: 14px;
    padding-left: 10px;
  }

  .filter-value-delete {
    position: absolute;
    top: -8px;
    right: -8px;
  }

  .filter-value-delete:hover {
    cursor: pointer;
  }

  .clear-all-filters:hover {
    cursor: pointer;
  }
}
.mars-scheduler-filter >>> .container-filter-main-menu {
  min-width: 250px;
  left: -17px;
}
.vertical-separator {
  width: 1px;
  border-left: 1px solid;
  height: 100%;
}
.color-filter {
  color: #909db8;
}
</style>
